/**
 * Equipment Management System
 * Tracks equipment inventory, maintenance, parts, and locations
 */

export interface Equipment {
  id: string;
  type: 'pump' | 'compressor' | 'separator' | 'tank' | 'valve' | 'motor' | 'generator' | 'other';
  name: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  purchaseDate: Date;
  purchasePrice: number;
  currentValue: number;
  status: 'available' | 'in-use' | 'maintenance' | 'retired' | 'broken';
  location: {
    type: 'warehouse' | 'well' | 'shop' | 'vendor';
    wellId?: string;
    wellName?: string;
    address?: string;
    coordinates?: { lat: number; lng: number };
  };
  maintenanceHistory: MaintenanceRecord[];
  nextMaintenanceDue?: Date;
  maintenanceInterval: number; // days
  specifications?: Record<string, any>;
  notes?: string;
  photos?: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface MaintenanceRecord {
  id: string;
  equipmentId: string;
  date: Date;
  type: 'routine' | 'repair' | 'inspection' | 'replacement' | 'emergency';
  description: string;
  performedBy: string;
  performedByName: string;
  cost: number;
  partsUsed: PartUsage[];
  hoursSpent: number;
  notes?: string;
  photos?: string[];
  nextDueDate?: Date;
  completed: boolean;
  createdAt: Date;
}

export interface Part {
  id: string;
  partNumber: string;
  name: string;
  description: string;
  category: string;
  manufacturer: string;
  currentStock: number;
  minStock: number;
  maxStock: number;
  reorderPoint: number;
  unitCost: number;
  location: string;
  supplier?: string;
  notes?: string;
  compatibleEquipment: string[];
  lastRestocked?: Date;
  createdAt: Date;
}

export interface PartUsage {
  partId: string;
  partName: string;
  quantity: number;
  cost: number;
}

class EquipmentManager {
  private equipment: Equipment[] = [];
  private parts: Part[] = [];
  private maintenanceRecords: MaintenanceRecord[] = [];

  constructor() {
    this.loadFromStorage();
  }

  // ============= EQUIPMENT MANAGEMENT =============

  addEquipment(equipment: Omit<Equipment, 'id' | 'createdAt' | 'updatedAt'>): Equipment {
    const newEquipment: Equipment = {
      ...equipment,
      id: `equip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    this.equipment.push(newEquipment);
    this.saveToStorage();
    return newEquipment;
  }

  updateEquipment(id: string, updates: Partial<Equipment>): Equipment | null {
    const index = this.equipment.findIndex(e => e.id === id);
    if (index === -1) return null;

    this.equipment[index] = {
      ...this.equipment[index],
      ...updates,
      updatedAt: new Date(),
    };

    this.saveToStorage();
    return this.equipment[index];
  }

  deleteEquipment(id: string): boolean {
    const index = this.equipment.findIndex(e => e.id === id);
    if (index === -1) return false;

    this.equipment.splice(index, 1);
    this.saveToStorage();
    return true;
  }

  getEquipment(id: string): Equipment | null {
    return this.equipment.find(e => e.id === id) || null;
  }

  getAllEquipment(): Equipment[] {
    return [...this.equipment];
  }

  getEquipmentByType(type: Equipment['type']): Equipment[] {
    return this.equipment.filter(e => e.type === type);
  }

  getEquipmentByStatus(status: Equipment['status']): Equipment[] {
    return this.equipment.filter(e => e.status === status);
  }

  getEquipmentAtWell(wellId: string): Equipment[] {
    return this.equipment.filter(
      e => e.location.type === 'well' && e.location.wellId === wellId
    );
  }

  // ============= MAINTENANCE MANAGEMENT =============

  addMaintenanceRecord(record: Omit<MaintenanceRecord, 'id' | 'createdAt'>): MaintenanceRecord {
    const newRecord: MaintenanceRecord = {
      ...record,
      id: `maint-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date(),
    };

    this.maintenanceRecords.push(newRecord);

    // Update equipment
    const equipment = this.getEquipment(record.equipmentId);
    if (equipment) {
      equipment.maintenanceHistory.push(newRecord);
      
      if (record.completed && record.nextDueDate) {
        equipment.nextMaintenanceDue = record.nextDueDate;
      }
      
      equipment.updatedAt = new Date();
    }

    // Update parts inventory
    record.partsUsed.forEach(usage => {
      this.updatePartStock(usage.partId, -usage.quantity);
    });

    this.saveToStorage();
    return newRecord;
  }

  getMaintenanceHistory(equipmentId: string): MaintenanceRecord[] {
    return this.maintenanceRecords
      .filter(r => r.equipmentId === equipmentId)
      .sort((a, b) => b.date.getTime() - a.date.getTime());
  }

  getUpcomingMaintenance(daysAhead: number = 30): Equipment[] {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + daysAhead);

    return this.equipment.filter(e => {
      if (!e.nextMaintenanceDue) return false;
      return e.nextMaintenanceDue <= futureDate;
    }).sort((a, b) => {
      if (!a.nextMaintenanceDue || !b.nextMaintenanceDue) return 0;
      return a.nextMaintenanceDue.getTime() - b.nextMaintenanceDue.getTime();
    });
  }

  getOverdueMaintenance(): Equipment[] {
    const now = new Date();
    return this.equipment.filter(e => {
      if (!e.nextMaintenanceDue) return false;
      return e.nextMaintenanceDue < now;
    });
  }

  // ============= PARTS INVENTORY =============

  addPart(part: Omit<Part, 'id' | 'createdAt'>): Part {
    const newPart: Part = {
      ...part,
      id: `part-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date(),
    };

    this.parts.push(newPart);
    this.saveToStorage();
    return newPart;
  }

  updatePart(id: string, updates: Partial<Part>): Part | null {
    const index = this.parts.findIndex(p => p.id === id);
    if (index === -1) return null;

    this.parts[index] = { ...this.parts[index], ...updates };
    this.saveToStorage();
    return this.parts[index];
  }

  updatePartStock(id: string, quantityChange: number): Part | null {
    const part = this.parts.find(p => p.id === id);
    if (!part) return null;

    part.currentStock += quantityChange;
    
    if (quantityChange > 0) {
      part.lastRestocked = new Date();
    }

    this.saveToStorage();
    return part;
  }

  getPart(id: string): Part | null {
    return this.parts.find(p => p.id === id) || null;
  }

  getAllParts(): Part[] {
    return [...this.parts];
  }

  getLowStockParts(): Part[] {
    return this.parts.filter(p => p.currentStock <= p.reorderPoint);
  }

  getPartsByEquipment(equipmentId: string): Part[] {
    return this.parts.filter(p => p.compatibleEquipment.includes(equipmentId));
  }

  // ============= ANALYTICS =============

  getEquipmentStatistics(): {
    total: number;
    byType: Record<string, number>;
    byStatus: Record<string, number>;
    totalValue: number;
    maintenanceCostThisMonth: number;
  } {
    const byType: Record<string, number> = {};
    const byStatus: Record<string, number> = {};
    let totalValue = 0;

    this.equipment.forEach(e => {
      byType[e.type] = (byType[e.type] || 0) + 1;
      byStatus[e.status] = (byStatus[e.status] || 0) + 1;
      totalValue += e.currentValue;
    });

    // Calculate this month's maintenance costs
    const thisMonth = new Date();
    thisMonth.setDate(1);
    thisMonth.setHours(0, 0, 0, 0);

    const maintenanceCostThisMonth = this.maintenanceRecords
      .filter(r => r.date >= thisMonth && r.completed)
      .reduce((sum, r) => sum + r.cost, 0);

    return {
      total: this.equipment.length,
      byType,
      byStatus,
      totalValue,
      maintenanceCostThisMonth,
    };
  }

  getMaintenanceCostByEquipment(equipmentId: string): number {
    return this.maintenanceRecords
      .filter(r => r.equipmentId === equipmentId && r.completed)
      .reduce((sum, r) => sum + r.cost, 0);
  }

  // ============= LOCATION TRACKING =============

  moveEquipment(
    equipmentId: string,
    location: Equipment['location']
  ): Equipment | null {
    const equipment = this.getEquipment(equipmentId);
    if (!equipment) return null;

    equipment.location = location;
    equipment.updatedAt = new Date();
    
    this.saveToStorage();
    return equipment;
  }

  getEquipmentHistory(equipmentId: string): Array<{
    date: Date;
    location: Equipment['location'];
    action: string;
  }> {
    // In production, this would track all location changes
    // For now, return maintenance history as movement history
    const maintenance = this.getMaintenanceHistory(equipmentId);
    
    return maintenance.map(m => ({
      date: m.date,
      location: this.getEquipment(equipmentId)?.location || { type: 'warehouse' as const },
      action: `Maintenance: ${m.type}`,
    }));
  }

  // ============= STORAGE =============

  private saveToStorage(): void {
    try {
      localStorage.setItem('equipment_data', JSON.stringify({
        equipment: this.equipment,
        parts: this.parts,
        maintenanceRecords: this.maintenanceRecords,
      }));
    } catch (error) {
      console.error('Failed to save equipment data:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('equipment_data');
      if (!stored) {
        this.initializeMockData();
        return;
      }

      const data = JSON.parse(stored);
      this.equipment = data.equipment || [];
      this.parts = data.parts || [];
      this.maintenanceRecords = data.maintenanceRecords || [];

      // Convert date strings back to Date objects
      this.equipment.forEach(e => {
        e.purchaseDate = new Date(e.purchaseDate);
        e.createdAt = new Date(e.createdAt);
        e.updatedAt = new Date(e.updatedAt);
        if (e.nextMaintenanceDue) {
          e.nextMaintenanceDue = new Date(e.nextMaintenanceDue);
        }
        e.maintenanceHistory.forEach(m => {
          m.date = new Date(m.date);
          m.createdAt = new Date(m.createdAt);
          if (m.nextDueDate) {
            m.nextDueDate = new Date(m.nextDueDate);
          }
        });
      });

      this.maintenanceRecords.forEach(r => {
        r.date = new Date(r.date);
        r.createdAt = new Date(r.createdAt);
        if (r.nextDueDate) {
          r.nextDueDate = new Date(r.nextDueDate);
        }
      });

      this.parts.forEach(p => {
        p.createdAt = new Date(p.createdAt);
        if (p.lastRestocked) {
          p.lastRestocked = new Date(p.lastRestocked);
        }
      });
    } catch (error) {
      console.error('Failed to load equipment data:', error);
      this.initializeMockData();
    }
  }

  private initializeMockData(): void {
    // Add sample equipment
    this.addEquipment({
      type: 'pump',
      name: 'Weatherford RPM-250',
      manufacturer: 'Weatherford',
      model: 'RPM-250',
      serialNumber: 'WF-2023-001',
      purchaseDate: new Date('2023-01-15'),
      purchasePrice: 45000,
      currentValue: 40000,
      status: 'in-use',
      location: {
        type: 'well',
        wellId: 'well-001',
        wellName: 'Well 001',
      },
      maintenanceHistory: [],
      maintenanceInterval: 90,
      specifications: {
        flowRate: '250 BPD',
        pressure: '1500 PSI',
        power: '50 HP',
      },
    });

    // Add sample parts
    this.addPart({
      partNumber: 'SEAL-001',
      name: 'Pump Seal Kit',
      description: 'High-pressure seal kit for RPM series pumps',
      category: 'Seals & Gaskets',
      manufacturer: 'Weatherford',
      currentStock: 15,
      minStock: 5,
      maxStock: 30,
      reorderPoint: 8,
      unitCost: 125,
      location: 'Warehouse A-12',
      compatibleEquipment: [],
    });
  }
}

export const equipmentManager = new EquipmentManager();
